import React, { useEffect } from 'react';
import { 
  X, CheckCircle, Lock, Shield, Sparkles, Heart, AlertTriangle, 
  Users, Watch, Power, Volume2, Zap, Mic, Video 
} from 'lucide-react';
import { checkUnlockPrerequisite } from '../../utils/xpProgression';
import useDragToScroll from '../../hooks/useDragToScroll';

const ICON_MAP = {
  Users,
  Watch,
  Power,
  Volume2,
  Zap,
  Mic,
  Video,
};

const TileDetailModal = ({
  isOpen,
  onClose,
  tileInfo,
  isUnlocked,
  userXP = 0,
  onUnlock,
  trustedContactsCount = 0,
  onAddContact,
  unlockedFeatures = [],
}) => {
  const containerRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const cardRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        if (cardRef.current) {
          cardRef.current.scrollTop = 0;
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, tileInfo]);

  if (!isOpen || !tileInfo) return null;

  const IconComponent = ICON_MAP[tileInfo.iconName] || Shield;
  const prereq = checkUnlockPrerequisite(tileInfo.id, unlockedFeatures);
  const isPrereqMet = prereq.met;
  const canAfford = userXP >= tileInfo.costXP && isPrereqMet;

  return (
    <div 
      ref={containerRef}
      className="tile-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(3, 7, 18, 0.65)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        borderRadius: '16px',
        padding: '8px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <style>{`
        @media (max-width: 860px) {
          .tile-modal-overlay {
            align-items: flex-start !important;
            padding-top: 6px !important;
          }
        }
      `}</style>
      <div
        ref={cardRef}
        style={{
          background: 'linear-gradient(160deg, rgba(15, 29, 50, 0.98) 0%, rgba(6, 11, 20, 0.99) 100%)',
          border: '2px solid ' + (isUnlocked ? '#10b981' : 'rgba(242, 184, 7, 0.6)'),
          borderRadius: '14px',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '14px 16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.85)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* Bouton Fermer */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: 'none',
            color: '#cbd5e1',
            borderRadius: '50%',
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <X size={18} />
        </button>

        {/* EN-TÊTE DE LA TUILE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '16px' }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: isUnlocked ? 'rgba(16, 185, 129, 0.25)' : 'rgba(242, 184, 7, 0.2)',
            border: '1.5px solid ' + (isUnlocked ? '#10b981' : 'var(--mga-gold)'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconComponent size={28} color={isUnlocked ? '#4ade80' : 'var(--mga-gold)'} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{
                background: tileInfo.category === 'alert' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(6, 182, 212, 0.2)',
                color: tileInfo.category === 'alert' ? '#f87171' : '#67e8f9',
                border: '1px solid ' + (tileInfo.category === 'alert' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(6, 182, 212, 0.4)'),
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '0.68rem',
                fontWeight: 900,
                textTransform: 'uppercase',
              }}>
                {tileInfo.category === 'alert' ? '🚨 DÉCLENCHEMENT D’ALERTE' : '🔒 AIDE À L’ENQUÊTE & À L’INTERVENTION'}
              </span>
              <span style={{
                background: isUnlocked ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                color: isUnlocked ? '#4ade80' : '#f87171',
                border: '1px solid ' + (isUnlocked ? '#10b981' : 'rgba(239, 68, 68, 0.4)'),
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '0.68rem',
                fontWeight: 900,
              }}>
                {isUnlocked ? (tileInfo.id === 'contacts' ? `🟢 ${trustedContactsCount} CONTACT(S)` : '🟢 ACTIF & DÉVERROUILLÉ') : '🔒 NON DÉBLOQUÉ'}
              </span>
            </div>
            <h3 style={{ fontSize: '1.25rem', color: '#ffffff', margin: 0, fontWeight: 900 }}>
              {tileInfo.title}
            </h3>
          </div>
        </div>

        {/* SECTION 1 : À QUOI ÇA SERT & DESCRIPTION */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '14px',
          padding: '14px 16px',
        }}>
          <span style={{ fontSize: '0.74rem', color: 'var(--mga-gold)', fontWeight: 800, textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
            💡 Rôle & Fonctionnement
          </span>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#e2e8f0', lineHeight: 1.5 }}>
            {tileInfo.usage}
          </p>
        </div>

        {/* SECTION 2 : COÛTS EN RESSOURCES (XP & VITALITÉ PV) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{
            background: 'rgba(242, 184, 7, 0.08)',
            border: '1px solid rgba(242, 184, 7, 0.3)',
            borderRadius: '12px',
            padding: '12px',
          }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--mga-gold)', fontWeight: 800, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Sparkles size={13} /> {tileInfo.id === 'contacts' ? 'Coût par Contact' : 'Coût de Déblocage'}
            </span>
            <strong style={{ fontSize: '1.1rem', color: '#ffffff', display: 'block', marginTop: '4px' }}>
              {tileInfo.costXP} XP {tileInfo.id === 'contacts' ? '/ contact' : ''}
            </strong>
          </div>

          <div style={{
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '12px',
          }}>
            <span style={{ fontSize: '0.7rem', color: '#86efac', fontWeight: 800, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Heart size={13} /> Utilisation en Combat
            </span>
            <strong style={{ fontSize: '1.05rem', color: '#4ade80', display: 'block', marginTop: '4px' }}>
              ⚡ Instantané (Gratuit)
            </strong>
          </div>
        </div>

        {/* CONDITION / PRÉREQUIS DE DÉBLOCAGE SI NON SATISFAIT */}
        {!isUnlocked && !isPrereqMet && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1.5px solid #f59e0b',
            borderRadius: '14px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <AlertTriangle size={22} color="#fbbf24" style={{ flexShrink: 0 }} />
            <div>
              <strong style={{ fontSize: '0.84rem', color: '#fef08a', display: 'block' }}>
                🔒 Condition de déblocage préalable requise :
              </strong>
              <span style={{ fontSize: '0.78rem', color: '#fde68a', lineHeight: 1.35 }}>
                {prereq.reason} (Requis : <strong>{prereq.requiredTitle}</strong>).
              </span>
            </div>
          </div>
        )}

        {/* SECTION 3 : FORCES & AVANTAGES */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '14px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <span style={{ fontSize: '0.74rem', color: '#4ade80', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle size={15} /> Forces & Avantages Majeurs
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tileInfo.forces?.map((force, idx) => (
              <div key={`force-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#d1fae5', lineHeight: 1.4 }}>
                <span style={{ color: '#4ade80', fontWeight: 900 }}>•</span>
                <span>{force}</span>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 4 : FAIBLESSES & LIMITES D'UTILISATION */}
        <div style={{
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '14px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <span style={{ fontSize: '0.74rem', color: '#fbbf24', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={15} /> Faiblesses & Conditions d'Efficacité
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tileInfo.faiblesses?.map((faiblesse, idx) => (
              <div key={`faiblesse-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#fef3c7', lineHeight: 1.4 }}>
                <span style={{ color: '#fbbf24', fontWeight: 900 }}>•</span>
                <span>{faiblesse}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ACTIONS */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
          {tileInfo.id === 'contacts' ? (
            <button
              onClick={() => {
                if (trustedContactsCount < 25 && userXP >= 10 && onAddContact) {
                  onAddContact();
                }
              }}
              disabled={trustedContactsCount >= 25 || userXP < 10}
              style={{
                flex: 1,
                background: trustedContactsCount >= 25
                  ? 'rgba(16, 185, 129, 0.25)'
                  : userXP >= 10 
                  ? 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)' 
                  : 'rgba(239, 68, 68, 0.2)',
                color: trustedContactsCount >= 25 ? '#86efac' : userXP >= 10 ? '#000000' : '#fca5a5',
                border: trustedContactsCount >= 25 
                  ? '1px solid #10b981' 
                  : userXP >= 10 ? 'none' : '1px solid rgba(239, 68, 68, 0.4)',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 900,
                fontSize: '0.95rem',
                cursor: trustedContactsCount < 25 && userXP >= 10 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Users size={16} />
              <span>
                {trustedContactsCount >= 25
                  ? `✓ Limite maximale atteinte (25 / 25 contacts)`
                  : userXP >= 10 
                  ? `➕ Ajouter 1 Contact de Confiance (10 XP) • Total : ${trustedContactsCount}/25` 
                  : `XP Insuffisante (Requis : 10 XP | Solde : ${userXP} XP)`}
              </span>
            </button>
          ) : !isUnlocked ? (
            <button
              onClick={() => {
                if (canAfford && onUnlock) {
                  onUnlock(tileInfo.id, tileInfo.costXP, tileInfo.title);
                  onClose();
                }
              }}
              disabled={!canAfford}
              style={{
                flex: 1,
                background: canAfford 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                  : 'rgba(239, 68, 68, 0.2)',
                color: canAfford ? '#ffffff' : '#fca5a5',
                border: canAfford ? 'none' : '1px solid rgba(239, 68, 68, 0.4)',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 900,
                fontSize: '0.95rem',
                cursor: canAfford ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Lock size={16} />
              <span>
                {!isPrereqMet 
                  ? `🔒 Prérequis manquant (${prereq.requiredTitle})`
                  : canAfford 
                  ? `Débloquer cette Option (${tileInfo.costXP} XP)` 
                  : `XP Insuffisante (Requis : ${tileInfo.costXP} XP | Solde : ${userXP} XP)`}
              </span>
            </button>
          ) : (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 900,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              ✓ Option Activée (Fermer)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TileDetailModal;
